// client/src/App.tsx
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import './index.css';

// Import pages and components
import Landing from './pages/Landing';
import Home from './pages/Home';
import Onboarding from './pages/AddingClient';
import AdminPanel from './components/admin/AdminPanel';
import AssessorPage from './components/Assessor/AssessorPage';
import AssessmentPart1 from './components/Assessor/AssessmentPart1';
import AssessmentPart2 from './components/Assessor/AssessmentPart2';
import ContractPage from './components/ContractPage';
import ContractDeclined from './pages/ContractDeclined';
import ContractAccepted from './components/ContractAccepted';
import ContractLinkExpired from './components/ContractLinkExpired';
import PaymentSuccess from './pages/PaymentSuccess';
import ClientDetails from './components/Client/ClientDetails';
import CounsellorAllocationPage from './components/Client/CounsellorAllocationPage';
import CounsellorDetails from './components/Counsellor/CounsellorDetails';
import ContractViewerWrapper from './components/ContractViewerWrapper';
import CounsellorCreate from './pages/Counsellor/CounsellorCreation';
import AddExistingCounsellor from './pages/Counsellor/AddExistingCounsellor';
import CompleteProfile from './pages/Counsellor/CompleteProfile';
import CounsellorManagement from './pages/Counsellor/CounsellorManagement';
import ApplicationForm from './pages/Counsellor/ApplicationForm';
import ExistingClientOnboarding from './pages/Client/ExistingClientOnboarding';
import ApplicationInfoPage from './pages/Counsellor/ApplicationInfoPage';
import ApplicationDeclined from './pages/Counsellor/ApplicationDeclined';
import ApplicationSuccess from './pages/Counsellor/ApplicationSuccess';
import ApplicationFeedback from './pages/public/ApplicationFeedback';
import LegalForm from './pages/Counsellor/LegalForm';
import CredentialReactivation from './pages/CredentialReactivation';
import SuspendedCompliance from './pages/SuspendedCompliance';
import ReviewApplicationFormContainer from './components/Counsellor/ReviewApplicationFormContainer';
import OffBoarding from './pages/OffBoarding';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SetPassword from './pages/SetPassword';
import InitialSetup from './pages/InitialSetup';
import MfaSetup from './pages/MfaSetup';
import ChangePassword from './pages/ChangePassword';
import ProtectedRoute from './components/ProtectedRoute';
import ArchivedRecords from './pages/ArchivedRecords';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import InterviewManagement from './pages/Admin/InterviewManagement';
import CounsellorDashboard from './pages/CounsellorDashboard';
import SignAgreement from './pages/Counsellor/SignAgreement';
import SessionNotesContainer from './components/SessionNotes/SessionNotesContainer';
import HistoricalNotes from './pages/Counsellor/HistoricalNotes';
import AppointmentDashboard from './components/Appointments/AppointmentDashboard';
import Agent from './pages/Agent';
import { AgentProvider } from './agent/contexts/AgentContext';
import PaymentSettings from './pages/Admin/PaymentSettings';
import UtilisationReport from './pages/Admin/UtilisationReport';
import PaymentDashboard from './components/admin/PaymentDashboard';
import ApplicationLinkExpired from './pages/ApplicationLinkExpired';
import Calendar from './components/Calendar/Calendar';
import ReferenceForm from './pages/Reference/ReferenceForm';
import ChristmasForm from './pages/ChristmasForm';
import CounsellorOnboardingDashboard from './components/Counsellor/CounsellorOnboardingDashboard';
import CounsellorLegalInformation from './components/Counsellor/LegalInformation';
import CounsellorReferences from './components/Counsellor/References';
import AdminDashboard from './pages/AdminDashboard';
import MTDDashboard from './pages/MTDDashboard';
import UserSettings from './pages/UserSettings';
import PoliciesAndProcedures from './pages/PoliciesAndProcedures';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { setupAxiosErrorInterceptor } from './utils/axiosErrorInterceptor';
import { appLogger, ErrorCategory } from './utils/errorLogger';

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Landing />} />
      <Route path="/application-info/:token" element={<ApplicationInfoPage />} />
      <Route path="/application-form/:token" element={<ApplicationForm />} />
      <Route path="/application-declined" element={<ApplicationDeclined />} />
      <Route path="/application-success" element={<ApplicationSuccess />} />
      <Route path="/application-feedback/:token" element={<ApplicationFeedback />} />
      <Route path="/legal-form/:token" element={<LegalForm />} />
      <Route path="/reactivate/:token" element={<CredentialReactivation />} />
      <Route path="/application-link-expired" element={<ApplicationLinkExpired />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/set-password/:token" element={<SetPassword />} />
      <Route path="/initial-setup" element={<InitialSetup />} />
      <Route path="/mfa-setup" element={<MfaSetup />} />
      <Route path="/contract/public/view/:token" element={<ContractPage />} />
      <Route path="/contract-accepted" element={<ContractAccepted />} />
      <Route path="/contract-declined" element={<ContractDeclined />} />
      <Route path="/contract-link-expired" element={<ContractLinkExpired />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/reference/:token" element={<ReferenceForm />} />
      <Route path="/contract-viewer/:clientId" element={<ContractViewerWrapper />} />
      <Route path="/christmas/:uniqueId" element={<ChristmasForm />} />

      {/* Protected routes */}
      <Route 
        path="/change-password" 
        element={
          <ProtectedRoute allowPasswordChange>
            <ChangePassword />
          </ProtectedRoute>
        } 
      />
      
      <Route
        path="/suspended"
        element={
          <ProtectedRoute>
            <SuspendedCompliance />
          </ProtectedRoute>
        }
      />

      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <UserSettings />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/existing-client-onboarding/:clientId" 
        element={
          <ProtectedRoute>
            <ExistingClientOnboarding />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminPanel />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin/payment-settings" 
        element={
          <ProtectedRoute>
            <PaymentSettings />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/admin/utilisation" 
        element={
          <ProtectedRoute>
            <UtilisationReport />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/agent" 
        element={
          <ProtectedRoute requiredRole="administrator">
            <AgentProvider>
              <Agent />
            </AgentProvider>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/assessor" 
        element={
          <ProtectedRoute>
            <AssessorPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/interview-management" 
        element={
          <ProtectedRoute requiredRole="administrator">
            <InterviewManagement />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute requiredRole="administrator">
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/mtd" 
        element={
          <ProtectedRoute requiredRole={['administrator', 'counsellor']}>
            <MTDDashboard />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/calendar" 
        element={
          <ProtectedRoute>
            <Calendar />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/assessment-part-1/:clientId" 
        element={
          <ProtectedRoute>
            <AssessmentPart1 />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/assessment-part-2/:clientId" 
        element={
          <ProtectedRoute>
            <AssessmentPart2 />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/client-details/:id" 
        element={
          <ProtectedRoute requiredRole={["administrator", "counsellor"]}>
            <ClientDetails />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/clients/:clientId/allocate" 
        element={
          <ProtectedRoute requiredRole={["administrator"]}>
            <CounsellorAllocationPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellors" 
        element={
          <ProtectedRoute requiredRole={["administrator"]}>
            <Navigate to="/?tab=counsellors" replace />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellors/:id" 
        element={
          <ProtectedRoute requiredRole={["administrator", "counsellor"]}>
            <CounsellorDetails />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/contracts/:clientId" 
        element={
          <ProtectedRoute>
            <ContractPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellor-create" 
        element={
          <ProtectedRoute>
            <CounsellorCreate />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/add-existing-counsellor" 
        element={
          <ProtectedRoute>
            <AddExistingCounsellor />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellor/complete-profile/:token" 
        element={<CompleteProfile />} 
      />
      
      <Route 
        path="/counsellor-management" 
        element={
          <ProtectedRoute requiredRole="administrator">
            <CounsellorManagement />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/review-application-form/:counsellorId" 
        element={
          <ProtectedRoute>
            <ReviewApplicationFormContainer />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/offboard/:type/:id" 
        element={
          <ProtectedRoute>
            <OffBoarding />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/archived-records" 
        element={
          <ProtectedRoute>
            <ArchivedRecords />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellor/dashboard" 
        element={
          <ProtectedRoute>
            <CounsellorDashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellor/sign-agreement" 
        element={
          <ProtectedRoute requiredRole="counsellor">
            <SignAgreement />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellor/onboarding" 
        element={
          <ProtectedRoute requiredRole="counsellor">
            <CounsellorOnboardingDashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellor/policies"
        element={
          <ProtectedRoute requiredRole={["administrator", "counsellor"]}>
            <PoliciesAndProcedures />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellor/:id/legal" 
        element={
          <ProtectedRoute requiredRole="counsellor">
            <CounsellorLegalInformation />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellor/:id/references" 
        element={
          <ProtectedRoute requiredRole="counsellor">
            <CounsellorReferences />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/clients/:clientId/sessions" 
        element={
          <ProtectedRoute requiredRole={["administrator", "counsellor"]}>
            <SessionNotesContainer />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellor/clients/:clientId/historical-notes" 
        element={
          <ProtectedRoute requiredRole={["counsellor"]}>
            <HistoricalNotes />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/counsellor/appointments" 
        element={
          <ProtectedRoute requiredRole={["administrator", "counsellor"]}>
            <AppointmentDashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/payments" 
        element={
          <ProtectedRoute requiredRole="administrator">
            <PaymentDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Set up axios interceptor
    const removeAxiosInterceptor = setupAxiosErrorInterceptor();
    
    // Set up global error handlers
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      appLogger.error(reason instanceof Error ? reason : new Error(String(reason ?? 'Unhandled Promise Rejection')), {
        source: 'window-unhandled-rejection',
        category: ErrorCategory.CLIENT,
        path: window.location.pathname
      });
      // Don't prevent default to allow other handlers to process the error
    };

    const handleError = (event: ErrorEvent) => {
      appLogger.error(event.error instanceof Error ? event.error : new Error(event.message || 'Global Error'), {
        source: 'window-error',
        category: ErrorCategory.CLIENT,
        path: window.location.pathname,
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      });
      // Don't prevent default to allow other handlers to process the error
    };
    
    // Add global event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);
    
    // Clean up function
    return () => {
      removeAxiosInterceptor();
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <Router>
      <ErrorBoundary>
        <AuthProvider>
          <NotificationProvider>
            <AppRoutes />
          </NotificationProvider>
        </AuthProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;